import _ from 'lodash'
import $ from 'jquery'
import FileSaver from 'file-saver'
import HttpStatus from 'http-status-codes'
import scoreboardUrl from 'scoreboard/components/scoreboard-url'
import appDisplay from 'scoreboard/components/app-display'
import ErrorView from 'scoreboard/components/error-view'

// These are injected in beginning.jsp from the Request attributes that Spring
// sets in the CsrfFilter, which is a feature of Spring Security
// see com.spider.cms.security.WebSecurityConfig for more implementation details
let csrfHeader, csrfToken

/**
 * Encapsulates concerns that must be applied to XHR Requests
 * 1. CSRF Tokens
 * 2. File (blob) downloads
 *
 * #26037 This class wraps a XMLHttpRequest rather than extending it
 * https://stackoverflow.com/a/4932156
 *
 * All native properties and functions may not be available.
 * Attributes like status, response, etc. are available.
 * But things like event listeners (onloadstart) should be
 * encapsulated inside this class and exposed as the required functionality
 * (e.g. download file)
 *
 */
class Xhr {

	/**
	 * A XHR request with special CSRF powers.
	 *
	 * @param {XMLHttpRequest} [options.xhr=new XMLHttpRequest()] Optionally, provide
	 * an existing xhr instance, wrapping it with this classes behavior.
	 *
	 * @param {String} [options.url] Relative url (e.g. 'api/...')
	 * 	If provided the request will be opened and the CSRF token header will be set
	 *
	 * @param {String} [options.method=GET]
	 */
	constructor(options = {}) {
		if (options.xhr) {
			this._xhr = options.xhr
		} else {
			this._xhr = new XMLHttpRequest()
		}

		const {method = 'GET', url} = options
		if (url) {
			this._xhr.open(method, scoreboardUrl(url))
			Xhr.setCsrfToken(this._xhr)
		}
	}

	// XMLHttpRequest properties we want to expose, (includes everything used by XhrStream)

	set onreadystatechange(eventHandler) {
		this._xhr.onreadystatechange = eventHandler
	}

	get readyState() {
		return this._xhr.readyState
	}

	get response() {
		return this._xhr.response
	}

	get responseText() {
		return this._xhr.responseText
	}

	/**
	 * @returns {Object} if no response an empty object is returned for convenience
	 */
	get responseJSON() {
		return _getResponseJSON(this._xhr)
	}

	get status() {
		return this._xhr.status
	}

	get statusText() {
		return this._xhr.statusText
	}

	get upload() {
		return this._xhr.upload
	}

	// XMLHttpRequest functions we want to expose
	abort() {
		this.aborted = true
		return XMLHttpRequest.prototype.abort.apply(this._xhr, arguments)
	}

	getAllResponseHeaders() {
		return XMLHttpRequest.prototype.getAllResponseHeaders.apply(this._xhr, arguments)
	}

	getResponseHeader() {
		return XMLHttpRequest.prototype.getResponseHeader.apply(this._xhr, arguments)
	}

	setRequestHeader() {
		return XMLHttpRequest.prototype.setRequestHeader.apply(this._xhr, arguments)
	}

	send() {
		return XMLHttpRequest.prototype.send.apply(this._xhr, arguments)
	}

	// Functions we want to extend XMLHttpRequest with

	/**
	 * If an endpoint streams a file (CSV, PDF, etc) this function can be used
	 * to capture the (blob) response and save the file inside the browser.
	 *
	 * @param {Function} options.url URL without the base (e.g. `api/section/....`)
	 *
	 * @param {Function} [options.method=GET]
	 *
	 * @param {Object} [options.headers={}]
	 *
	 * @param {Function} [options.error] An optional error handler, if none is provided
	 * the error response will be displayed in a standard ErrorView.
	 *
	 * @param {Function} [options.done] A callback for when the response is received
	 * (success or failure)
	 *
	 * @param {String} [options.filename] Default is to read the content-disposition
	 * header from the response.  If this option is provided, this value will be
	 * used instead.
	 *
	 * @param {String} [options.responseType = 'blob] If the file download is a
	 * JSON response, pass 'json'
	 */
	download(options = {}) {
		const {url, data, method = 'GET', filename, responseType = 'blob', headers = {}} = options
		// #25756 - https://stackoverflow.com/a/53390705
		if (responseType == 'blob') {
			this._xhr.onloadstart = () => this._xhr.responseType = 'blob'
		}

		return $.ajax({
			url,
			headers,
			method,
			data,
			xhr: () => this._xhr,
			success: data => {
				if (options.success) {
					options.success(data)
				} else {
					this.saveBlob(data, responseType, filename)
				}
				_.invoke(options, 'done')
			},
			error: () => this.readErrorResponse().then(response => {
				if (options.error) {
					options.error(response)
				} else {
					appDisplay.dialog(new ErrorView(response))
				}
				_.invoke(options, 'done')
			})
		})
	}

	saveBlob(data, responseType, filename) {
		let blob = data
		if (responseType == 'json') {
			let json = JSON.stringify(data)
			blob = new Blob([json], {type: 'application/octet-stream'})
		}
		// #26814 handling special characters in filename:
		// Filesaver is using the download property of an <a>
		// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a
		// which specifies that / and \ characters are converted to underscores
		// (_). In addition, "Filesystems may forbid other characters in
		// filenames, so browsers will adjust the suggested name if necessary."
		FileSaver.saveAs(blob, _.defaultTo(filename, this.getFilename()))
	}

	/**
	 * Extract the filename from the Content-Disposition header of the response
	 */
	getFilename() {
		let filename
		const disposition = this.getResponseHeader('Content-Disposition')
		if (disposition && disposition.indexOf('filename') !== -1) {
			const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
			const matches = filenameRegex.exec(disposition)
			if (matches != null && matches[1]) {
				filename = matches[1].replace(/['"]/g, '')
			}
		}
		return filename
	}

	/**
	 * @returns {Promise<Object>} A promise that is fulfilled with a JSON object
	 * containing the response attributes (e.g. messageCode, stackTrace, etc)
	 */
	readErrorResponse() {
		return new Promise((resolve) => {
			// This is the only way to read a Blob which we have to do since
			// the request was setup with responseType = 'blob' above
			const reader = new FileReader()
			reader.addEventListener('loadend', () => {
				this._xhr._responseTextFromBlob = reader.result
				this._xhr._responseJSONFromBlob = JSON.parse(reader.result)
				resolve(this._xhr._responseJSONFromBlob)
			})
			reader.readAsText(this._xhr.response)
		})
	}

	// Static Functions

	static setCsrfToken(req) {
		if (csrfHeader && csrfToken) {
			req.setRequestHeader(csrfHeader, csrfToken)
		}
	}

	/**
	 *
	 * @param {Array} reqHeaders header name/value pairs, the csrf token header
	 * will be added and returned
	 */
	static setCsrfTokenInHeaderArray(reqHeaders) {
		reqHeaders[csrfHeader] = csrfToken
		return reqHeaders
	}

	/**
	 * @param {XMLHttpRequest} xhr
	 * @returns {boolean} true if the XHR response indicates that a login is
	 * required as a result of an invalid session or csrf token.
	 */
	static requiresLogin(xhr) {
		const loginRequired =
			xhr.status == HttpStatus.UNAUTHORIZED ||
			xhr.status == HttpStatus.FORBIDDEN // Missing/Invalid CSRF Tokens

		const xhrJson = _getResponseJSON(xhr)
		return loginRequired && xhrJson && xhrJson.messageCode === 'common.security.notLoggedIn'
	}
}

function _getResponseJSON(xhr) {
	var responseJSON = xhr.responseJSON
	if (!responseJSON) {
		try {
			// An exception occurs if xhr.responseText is accesssed for Blobs
			// So we must preempt that here
			let responseText = xhr._responseTextFromBlob || xhr.responseText
			if (_.isNil(responseText)) {
				return {}
			}
			responseJSON = JSON.parse(responseText)
			xhr.responseJSON = responseJSON
		} catch (e) {
			responseJSON = {}
		}
	}
	return responseJSON
}

/**
 * Reads csrf header name and token value from the window, which is set by the
 * server in the JSP; this global state is cleared upon login.
 *
 * Allows reading the csrf token to be deferred incase a new token was issues
 * due to a session timeout on the login page (#26030)
 */
function setToken () {
	csrfHeader = window.csrfHeader
	csrfToken = window.csrf
}

/**
 * Checks if we are unable to communicate with the server
 *
 * @param xhr the ajax response
 * @returns {boolean} true if we are offline or the server is unreachable
 */
function isOffline (xhr) {
	// #31360 a status or zero is reported when a request is aborted, so we need
	// to make sure we don't treat aborts as being offline
	return (!xhr || xhr.status === 0 || xhr.status === 503) && (!xhr || xhr.statusText !== 'abort')
}

export default Xhr
export { setToken, isOffline }
Xhr.__filename__ = 'components/xhr.js'
